import { CheckCircle2, Download, HelpCircle, GraduationCap } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Helmet } from "react-helmet-async"
import heroImage from "../../assets/TopHeroImages/2.png"

const DOCUMENTS_INSCRIPTION = [
        "Une photocopie de diplôme ou certificat du post-fondamental certifié conforme à l'original ;",
        "Une attestation de réussite à l'Examen d'État (pour les BAC) ou certificat de participation à l'Examen d'État pour les Instituts supérieurs professionnels ;",
        "La photocopie et l'original du bulletin de la dernière année du post-fondamental (enseignement général ou enseignement technique) ;",
        "La photocopie de la pièce d'identité ;",
        "Le curriculum vitae actualisé et signé ;",
        "L'extrait d'acte de naissance ;",
        "Trois (3) photos passeport ;",
        "Versement des frais d'inscription à la caisse du Service des finances."
    ]

const DOCUMENTS_MASTERS = [
        "Une copie de diplôme de Baccalauréat / Licence / Ingéniorat ou son équivalent (certifiée conforme à l'original) ;",
        "Une copie du Diplôme d'État (certifiée conforme à l'original) ;",
        "Tous les bulletins du premier cycle (Baccalauréat / Licence / Ingéniorat, …) ;",
        "Photocopie de la Carte nationale d'identité ou du Passeport ;",
        "Un CV actualisé et signé ;",
        "Une lettre de motivation adressée au Recteur de l'Université de Ngozi et précisant la filière de choix du candidat ;",
        "Trois photos passeport ;",
        "Un reçu de paiement des frais pour l'étude du dossier."
    ]

export default function Admission() {
    const steps = [
        { number: "01", title: "Préparer le dossier", description: "Rassemblez les pièces exigées selon votre niveau (inscription 1er cycle ou Master)." },
        { number: "02", title: "Dépôt des dossiers", description: "Déposez votre dossier complet au secrétariat de la faculté ou du service compétent." },
        { number: "03", title: "Étude du dossier", description: "L'Université étudie votre candidature et les frais d'étude du dossier sont à régler." },
        { number: "04", title: "Inscription définitive", description: "En cas d'acceptation, procédez au versement des frais d'inscription et à l'inscription." }
    ]

    return (
        <div className="min-h-screen bg-background font-sans text-foreground antialiased">
            <Helmet>
                <title>Admission & Inscriptions | Université de Ngozi</title>
                <meta name="description" content="Documents exigés pour l'inscription (1er cycle) et pour les Masters. Guide d'admission à l'Université de Ngozi." />
            </Helmet>
            {/* Hero Section */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {heroImage && <img src={heroImage} alt="Admission - Université de Ngozi" className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105" />}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Future Étudiant</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Guide d'Admission</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Construire votre</span>
                            <span className="block text-white mt-2">Futur Académique</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Rejoignez une communauté d'excellence et donnez-vous les moyens de réussir vos ambitions professionnelles.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Télécharger le Guide
                                <Download className="w-5 h-5 ml-3" />
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Quick Stats Bar */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "2025", label: "Prochaine Rentrée" },
                        { val: "100%", label: "En Ligne" },
                        { val: "SEPT", label: "Clôture" },
                        { val: "AIDE", label: "Guichet Unique" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {["Étapes", "Documents 1er cycle", "Documents Masters", "Candidats étrangers", "Support"].map((item, i) => (
                                <a key={i} href={`#${item === "Documents 1er cycle" ? "documents-inscription" : item === "Documents Masters" ? "documents-masters" : item === "Candidats étrangers" ? "etrangers" : item === "Support" ? "support" : "etapes"}`} className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item}
                                </a>
                            ))}
                        </nav>
                    </aside>

                    {/* Main Content */}
                    <main className="space-y-16">
                        {/* Section 01 - Steps */}
                        <section id="etapes">
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Le Parcours d'Admission
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                {steps.map((step, idx) => (
                                    <div key={idx} className="interactive-card p-6 border-l-4 border-primary group">
                                        <div className="text-3xl font-black text-primary/10 mb-4 group-hover:text-accent transition-smooth">{step.number}</div>
                                        <h3 className="text-sm font-bold uppercase tracking-tight mb-2">{step.title}</h3>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            {step.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Section 02 - Documents à l'inscription (1er cycle) */}
                        <section id="documents-inscription">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Documents exigés à la prise d'inscription (1er cycle)
                            </h2>
                            <p className="text-sm text-muted-foreground mb-6">Les pièces à fournir pour l'inscription en premier cycle (Baccalauréat, Instituts supérieurs professionnels, etc.) :</p>
                            <div className="bg-primary/5 p-8 border border-primary/10 rounded-xl">
                                <ul className="space-y-3">
                                    {DOCUMENTS_INSCRIPTION.map((item, idx) => (
                                        <li key={idx} className="flex items-start gap-3">
                                            <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                                            <span className="text-sm font-medium text-foreground/90">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>

                        {/* Section 03 - Documents pour les Masters */}
                        <section id="documents-masters">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Documents exigés pour les Masters
                            </h2>
                            <p className="text-sm text-muted-foreground mb-6">Pour les candidatures en Master, les documents suivants sont requis :</p>
                            <div className="bg-primary/5 p-8 border border-primary/10 rounded-xl">
                                <div className="flex items-center gap-3 mb-6">
                                    <GraduationCap className="w-6 h-6 text-primary" />
                                    <span className="text-[10px] font-black uppercase tracking-widest text-primary">POUR LES MASTERS</span>
                                </div>
                                <ul className="space-y-3">
                                    {DOCUMENTS_MASTERS.map((item, idx) => (
                                        <li key={idx} className="flex items-start gap-3">
                                            <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                                            <span className="text-sm font-medium text-foreground/90">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>

                        {/* Section 04 - Candidats étrangers */}
                        <section id="etrangers">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Candidats étrangers
                            </h2>
                            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
                                <p className="text-sm font-semibold text-foreground mb-2">Note importante</p>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Pour les étrangers, il est exigé une <strong className="text-foreground">attestation d'équivalence des titres académiques</strong>, délivrée par le Ministère de l'Éducation Nationale et de la Recherche Scientifique de la République du Burundi.
                                </p>
                            </div>
                        </section>

                        {/* Section 04 - Support */}
                        <section id="support">
                            <div className="bg-accent/5 p-8 border-l-8 border-accent">
                                <h3 className="heading-4 text-foreground mb-6 flex items-center gap-2">
                                    <HelpCircle className="w-5 h-5 text-accent" /> Besoin d'Aide ?
                                </h3>
                                <div className="grid md:grid-cols-3 gap-8">
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-black uppercase text-accent tracking-widest">Email</p>
                                        <p className="text-xs font-bold text-foreground">uningozi@univ-ngozi.bi</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-black uppercase text-accent tracking-widest">Téléphone</p>
                                        <p className="text-xs font-bold text-foreground">+257 22 30 22 59</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-black uppercase text-accent tracking-widest">Bureau</p>
                                        <p className="text-xs font-bold text-foreground">Secrétariat Général</p>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </article>

            {/* CTA Footer */}
            <footer className="bg-primary text-white border-t-8 border-accent">
                <div className="container py-16 text-center max-w-2xl">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black mb-4 block">Prêt à entrer ?</span>
                    <h2 className="heading-2 mb-8 leading-tight">
                        Lancez votre carrière<br />dès aujourd'hui.
                    </h2>
                </div>
            </footer>
        </div>
    )
}
